import { CalloutComponent, FormValues, getValue, InputFormGroup } from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { Fields as UpdatePasswordFields, UpdatePassword } from "../../components/UpdatePassword/UpdatePassword";
import { parseId } from "../../helpers/idHelper";
import { validateVmDbPassword } from "../../helpers/validationHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_CDB_UPDATE_POLL_DELAY,
  VMDB_CDB_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface VmDbChangePasswordProps extends OperationActionProps {
  databaseId: string | undefined;
}

const VmDbChangePassword = (
  { targetId, location, databaseId, onExecute, onCancel }: VmDbChangePasswordProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName: databaseName } = parseId(databaseId);
  const resGroup = decodeURIComponent(resourceGroup || "");

  const { invokeCall: invokePasswordUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.updateVmdbDatabase },
  );
  const [passwordCalloutRef, setPasswordCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);

  const onUpdateCdbPassword = (formValues: FormValues): void => {
    const password = getValue<string>(formValues, UpdatePasswordFields.Password, InputFormGroup);

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: passwordCalloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateVmCdbPassword(),
          message: Messages.notifications.inProgress.messages.updateVmCdbPassword(databaseName),
        },
        success: {
          title: Messages.notifications.success.titles.updateVmCdbPassword(),
          message: Messages.notifications.success.messages.updateVmCdbPassword(databaseName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateVmCdbPassword(),
          message: Messages.notifications.failure.messages.updateVmCdbPassword(databaseName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.VMDB_CDB_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resGroup,
            databaseName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.VMDB_UPDATED_CHECK_CDB,
          delay: VMDB_CDB_UPDATE_POLL_DELAY,
          interval: VMDB_CDB_UPDATE_POLL_INTERVAL,
        },
      },
    };

    invokePasswordUpdate({
      databaseName,
      resourceGroupName: resGroup,
      subscriptionId,
      updateDatabaseDetails: { newAdminPassword: password },
    }, invokeOptions);
  };

  return (
    <UpdatePassword
      componentRef={setPasswordCalloutRef}
      targetId={targetId}
      onClose={onCancel}
      onSubmit={onUpdateCdbPassword}
      validator={validateVmDbPassword}
    />
  );
};

export const newVmDbChangePassword = (
  props: VmDbChangePasswordProps,
): JSX.Element => (<VmDbChangePassword {...props} />);
